import React, { useContext, useEffect, useState } from "react";
import jwt from "jsonwebtoken";
import { placeOutboundCall } from "../connectExtensions";
import { Backdrop, Box, Card, CardContent, CircularProgress, Grid, makeStyles, Typography } from "@material-ui/core";
import { CallContext } from "./CallContext";
import AppContext from "./AppContextProvider";

const loadToken = (): string => {
    const token = localStorage.getItem("ccpToken") || "";
    if (token) {
        const decoded = jwt.decode(token, { complete: true }) as any;
        const now = new Date().getTime();
        if (!decoded || decoded.payload.expires < now) {
            localStorage.removeItem("ccpToken");
        }
    }
    return token;
};

/**
 * Call this method when API Token becomes available
 */
const saveToken = (token: string): void => {
    localStorage.setItem("ccpToken", token);
};

const useStyles = makeStyles((theme) => ({
    backdrop: {
        zIndex: theme.zIndex.drawer + 1,
        color: "#fff",
    },
}));

export interface ITokenContext {
    token: string;
    busy: boolean;
    refresh: () => Promise<string>;
}

export const TokenContext = React.createContext<ITokenContext>(null as any);

/**
 * Tracks and refreshes the token
 * This feels like it should be in a API context
 */
export function TokenContextProvider(props: { children: any }) {
    const classes = useStyles();
    const [token, setToken] = useState(loadToken());
    const [busy, setBusy] = useState(false);

    const {
        config: { tokenAuthNumber },
        setAlert,
    } = useContext(AppContext);
    const { agent, contact } = useContext(CallContext);

    const updateToken = (next: string) => {
        saveToken(next);
        setToken(next);
    };

    const refresh = async (): Promise<string> => {
        let ccpToken = "";
        try {
            setBusy(true);
            setAlert("Authenticating...", "info");
            const result = await placeOutboundCall(agent, tokenAuthNumber);

            result.contact.onACW((c) => {
                c.clear({});
                setBusy(false);
            });

            const ccpToken = result.contact.getAttributes().ccpToken?.value;
            if (ccpToken) {
                updateToken(ccpToken);
                setAlert("Authentication successful", "success", 1000);
            } else {
                setAlert("Authentication error: Attribute named ccpToken is not available", "error");
            }

            //result.connection.destroy();
        } catch (error) {
            const message = typeof error === "string" ? JSON.parse(error).message : (error as any).message;
            setAlert(`Authentication error: ${message}`, "error");
            setBusy(false);
        }
        return ccpToken;
    };

    useEffect(() => {
        if (agent && !token && tokenAuthNumber) {
            const status = agent.getStatus().name;
            if (status === "Offline" || status === "Available") {
                refresh();
            }
        }
    }, [agent, token]);

    /**
     * Refresh token when it becomes available.
     */
    useEffect(() => {
        if (contact) {
            contact.onConnected((c) => {
                const ccpToken = c.getAttributes().ccpToken?.value;
                if (ccpToken && ccpToken !== token) {
                    updateToken(ccpToken);
                }
            });
        }
    }, [contact]);

    const ctx: ITokenContext = {
        token,
        busy,
        refresh,
    };

    return (
        <TokenContext.Provider value={ctx}>
            {props.children}
            <Backdrop className={classes.backdrop} open={busy}>
                <Card style={{ width: 600 }}>
                    <CardContent>
                        <Typography align="center" gutterBottom variant="h5" component="div">
                            Authenticating your session....
                        </Typography>
                        <Typography align="center" variant="body2">
                            Please wait while the system places an automated call and authenticates your session.
                        </Typography>
                    </CardContent>
                    <Box m={6} display="flex" alignItems="center" flexDirection="column" justifyContent="center">
                        <CircularProgress size="75px" color="inherit" />
                    </Box>
                </Card>
            </Backdrop>
        </TokenContext.Provider>
    );
}

export default TokenContext;
